/**
 * Parser for song lyrics with chord notation
 * Supports two formats:
 * 1. Bracket notation: Lovsyng [F]Gud, å min [C]sjel (kun […] som er gyldige akkorder — ikke [Chorus] som seksjon)
 * 2. Chord-line format: Chords on separate line above lyrics
 *
 * Hele filen skal ikke lenger tvinges inn i «bare bracket»-modus når [Chorus] finnes.
 * Seksjonslinjer [Verse 1], [Chorus] håndteres som seksjonsmarkører; akkord-linje + tekst parses som leadsheet.
 */

export interface ChordPosition {
  chord: string;
  position: number; // Character position in the original chord line (for pixel calculation)
}

export interface ParsedLine {
  text: string;
  chords: ChordPosition[];
  isSection: boolean; // True for Intro, Verse, Chorus, etc.
}

/**
 * Enkelt akkordsymbol (brukes for inline [Akkord] og for å filtrere bort [Chorus] osv.)
 * Merk: Må samsvare med mønsteret i isChordLine for konsistent deteksjon.
 */
function isChordToken(token: string): boolean {
  if (!token.trim()) return false;
  const chordPattern = /^[A-G][b#]?(\d+)?(m|maj|min|dim|aug|sus\d*)?(\d+)?(\/[A-G][b#]?)?$/;
  return chordPattern.test(token.trim());
}

/**
 * True bare hvis teksten har minst en [hakeparantes] med faktiske akkorder, f.eks. [C] eller [Am] [F].
 * Eksempler som IKKE skal telle: [Chorus] (C+horus), [Bridge] (B+ridge), [Verse 1] (V+…)
 * — de matcher gammel regex [A-G]… og tvinger feil «bracket-modus» for hele sangen, slik at
 * format med akkord-linje over tekst (leadsheet) aldri parses.
 */
function hasInlineBracketChords(text: string): boolean {
  const re = /\[([^\]]+)\]/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const inner = m[1].trim();
    if (!inner) continue;
    const tokens = inner.split(/\s+/).filter(Boolean);
    if (tokens.length > 0 && tokens.every((t) => isChordToken(t))) {
      return true;
    }
  }
  return false;
}

/**
 * Check if a line is likely a chord line
 * A chord line contains chord-like patterns separated by spaces
 */
function isChordLine(line: string): boolean {
  if (!line.trim()) return false;
  
  // Enhanced pattern for chord notation with support for:
  // - Basic chords: A-G
  // - Accidentals: b, #
  // - Numbers before modifiers: G2, Am2
  // - Modifiers: m, maj, min, dim, aug, sus, sus2, sus4
  // - Numbers after modifiers: 7, 9, 11, 13
  // - Slash chords: /A, /G#
  const chordPattern = /^[A-G][b#]?(\d+)?(m|maj|min|dim|aug|sus\d*)?(\d+)?(\/[A-G][b#]?)?$/;
  
  const tokens = line.trim().split(/\s+/);
  
  if (tokens.length === 0) return false;
  
  // Count how many tokens look like chords
  const chordCount = tokens.filter(token => chordPattern.test(token)).length;
  
  // If at least 50% of tokens are chords, consider it a chord line
  return chordCount >= Math.max(1, tokens.length * 0.5);
}

/**
 * Check if a line is a section marker like [Intro], [Verse], [Chorus]
 * Or a plain text section like "Intro", "Verse 1", etc.
 */
function isSectionMarker(line: string): boolean {
  const trimmed = line.trim();
  
  // Bracket format: [Intro], [Verse 1]
  if (/^\[[\w\s-]+\]$/.test(trimmed)) {
    return true;
  }
  
  // Plain format: Must be SHORT and match section keywords
  // Reject lines that are too long (likely lyrics, not section markers)
  if (trimmed.length > 30) {
    return false;
  }
  
  // Must be at start of line and contain ONLY the section word (with optional number)
  const lowerLine = trimmed.toLowerCase();
  
  // Strict matching - must START with section keyword
  const sectionPatterns = [
    /^intro\s*\d*$/,
    /^verse\s*\d*$/,
    /^chorus\s*\d*$/,
    /^bridge\s*\d*$/,
    /^outro\s*\d*$/,
    /^pre-chorus\s*\d*$/,
    /^refrain\s*\d*$/,
    /^tag\s*\d*$/,
    /^coda\s*\d*$/
  ];
  
  return sectionPatterns.some(pattern => pattern.test(lowerLine));
}

/**
 * Parse chord-line format into ParsedLine
 * Takes a chord line and the corresponding lyric line (which may be empty)
 */
function parseChordLine(chordLine: string, lyricLine: string): ParsedLine {
  const chords: ChordPosition[] = [];
  
  if (!chordLine.trim()) {
    return {
      text: lyricLine,
      chords: [],
      isSection: false
    };
  }
  
  // Enhanced chord pattern matching
  const chordPattern = /[A-G][b#]?(\d+)?(m|maj|min|dim|aug|sus\d*)?(\d+)?(\/[A-G][b#]?)?/g;
  let match;
  
  while ((match = chordPattern.exec(chordLine)) !== null) {
    const chordText = match[0];
    const position = match.index; // Use chord line position directly
    
    chords.push({
      chord: chordText,
      position: position // Keep original chord line position for accurate pixel calculation
    });
  }
  
  return {
    text: lyricLine,
    chords,
    isSection: false
  };
}

/**
 * Parse a line of text with bracket notation [C], [Am], etc.
 * Returns the plain text and chord positions
 */
function parseBracketLine(line: string): ParsedLine {
  const chords: ChordPosition[] = [];
  let plainText = '';
  let currentPos = 0;
  
  // Match all [chord] patterns
  const regex = /\[([^\]]+)\]/g;
  let match;
  let lastIndex = 0;
  
  while ((match = regex.exec(line)) !== null) {
    // Add text before the chord
    const textBefore = line.substring(lastIndex, match.index);
    plainText += textBefore;
    currentPos += textBefore.length;
    
    // Add chord at current position
    chords.push({
      chord: match[1],
      position: currentPos
    });
    
    lastIndex = regex.lastIndex;
  }
  
  // Add remaining text
  plainText += line.substring(lastIndex);
  
  return {
    text: plainText,
    chords,
    isSection: false
  };
}

/**
 * Parse entire text with multiple lines
 * Automatically detects format (bracket notation or chord-line)
 */
export function parseText(text: string): ParsedLine[] {
  const lines = text.split('\n');
  const result: ParsedLine[] = [];
  
  let i = 0;
  while (i < lines.length) {
    const currentLine = lines[i];
    
    // Skip empty lines
    if (!currentLine.trim()) {
      result.push({
        text: '',
        chords: [],
        isSection: false
      });
      i++;
      continue;
    }
    
    // Seksjonsmarkører: [Verse 1], [Chorus] (før «feil» bracket-modus) — se note over isSectionMarker
    if (isSectionMarker(currentLine)) {
      // Display as section header
      result.push({
        text: currentLine.replace(/^\[|\]$/g, ''), // Remove brackets if present
        chords: [],
        isSection: true
      });
      i++;
      continue;
    }
    
    // Akkord-linje (E A C#m) over neste sanglinje
    if (isChordLine(currentLine)) {
      // Look ahead to find the next non-empty line (skip empty lines)
      let nextLineIndex = i + 1;
      let nextLine = '';
      
      // Skip empty lines to find actual lyrics
      while (nextLineIndex < lines.length) {
        const candidateLine = lines[nextLineIndex];
        if (candidateLine.trim()) {
          nextLine = candidateLine;
          break;
        }
        nextLineIndex++;
      }
      
      // Check if next non-empty line is a chord line or section marker
      const nextIsChordOrSection = !nextLine || isChordLine(nextLine) || isSectionMarker(nextLine);
      
      if (nextIsChordOrSection) {
        // Chord line without lyrics - display chords on their own line
        result.push(parseChordLine(currentLine, ''));
        i++;
      } else {
        // Chord line with lyrics - pair them together
        result.push(parseChordLine(currentLine, nextLine));
        
        // Skip all lines up to and including the lyric line
        i = nextLineIndex + 1;
      }
    } else {
      // Vanlige tekstlinjer, ev. med inline [C] / [Am] – ikke forveksl med [Chorus]
      if (hasInlineBracketChords(currentLine)) {
        result.push(parseBracketLine(currentLine));
      } else {
        result.push({
          text: currentLine,
          chords: [],
          isSection: false
        });
      }
      i++;
    }
  }
  
  return result;
}

// Export for backwards compatibility
export function parseLine(line: string): ParsedLine {
  return parseBracketLine(line);
}

